import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import type { TripStatus } from '@kmdx/contracts';
import { countryName, TRIP_STATUS_BG } from '@/i18n';
import { downloadCsv, euros, type CsvCell } from '@/lib/csv';
import { formatDate, formatKg, formatKm, formatMinor, formatNumber } from '@/lib/format';
import { useDrivers, usePartners, useVehicles } from '@/api/hooks/catalog';
import {
  useCustomersReport,
  useDriversReport,
  useTripsReport,
  useVansReport,
  type CustomersReportRow,
  type DriversReportRow,
  type TripsReportRow,
  type VansReportRow,
} from '@/api/hooks/reports';
import { StatusPill } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Select } from '@/components/ui/Field';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СПРАВКИТЕ ПО КУРСОВЕ, КЛИЕНТИ, ШОФЬОРИ И БУСОВЕ
 *
 *  Всяка е таблица ред по ред с ред „Общо" и бутон „Excel" — същите колони,
 *  които се виждат, без скритите за ролята (марж, заплата). Сумите в
 *  Excel-а са в евро с два знака, за да се смятат там.
 * ═══════════════════════════════════════════════════════════════════════════
 */

interface Column<T> {
  label: string;
  numeric?: boolean;
  /** Скрита за ролята — не се показва и не отива в Excel. */
  hidden?: boolean;
  cell: (row: T) => ReactNode;
  csv: (row: T) => CsvCell;
  total?: ReactNode;
  csvTotal?: CsvCell;
  className?: string;
}

function ReportTable<T>({
  title,
  subtitle,
  fileName,
  columns,
  rows,
  rowKey,
  empty = 'Няма данни за периода.',
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  fileName: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  empty?: string;
  actions?: ReactNode;
}) {
  const visible = columns.filter((c) => !c.hidden);
  const exportCsv = () =>
    downloadCsv(
      fileName,
      visible.map((c) => c.label),
      [...rows.map((row) => visible.map((c) => c.csv(row))), visible.map((c, i) => (i === 0 ? 'Общо' : (c.csvTotal ?? '')))],
    );
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={title}
        subtitle={subtitle}
        actions={
          <span className="flex flex-wrap items-center gap-2">
            {actions}
            <Button size="sm" variant="secondary" icon={<Download className="size-3.5" />} disabled={rows.length === 0} onClick={exportCsv}>
              Excel
            </Button>
          </span>
        }
      />
      <Table>
        <THead>
          <TR>
            {visible.map((c) => (
              <TH key={c.label} numeric={c.numeric} className="whitespace-nowrap">
                {c.label}
              </TH>
            ))}
          </TR>
        </THead>
        <TBody>
          {rows.length === 0 ? (
            <TableEmpty colSpan={visible.length}>{empty}</TableEmpty>
          ) : (
            rows.map((row) => (
              <TR key={rowKey(row)}>
                {visible.map((c) => (
                  <TD key={c.label} numeric={c.numeric} className={c.className}>
                    {c.cell(row)}
                  </TD>
                ))}
              </TR>
            ))
          )}
        </TBody>
        {rows.length ? (
          <tfoot>
            <TR className="bg-raised font-medium">
              {visible.map((c, i) => (
                <TD key={c.label} numeric={c.numeric}>
                  {i === 0 ? 'Общо' : (c.total ?? '')}
                </TD>
              ))}
            </TR>
          </tfoot>
        ) : null}
      </Table>
    </Card>
  );
}

const money = (minor: number | null | undefined) => formatMinor(minor ?? null, 'EUR', { compact: true });
const signed = (minor: number | null) => <span className={minor !== null && minor < 0 ? 'text-danger' : undefined}>{money(minor)}</span>;
const pct = (value: number | null) => (value === null ? '—' : `${value} %`);

/** ─── По курсове ─────────────────────────────────────────────────────── */

export function TripsReportView({ from, to }: { from: string; to: string }) {
  const [filters, setFilters] = useState({ vehicleId: '', driverId: '', partnerId: '' });
  const report = useTripsReport(from, to, filters);
  const vehicles = useVehicles();
  const drivers = useDrivers();
  const partners = usePartners();

  if (report.isLoading) return <LoadingBlock />;
  if (report.isError || !report.data) return <ErrorState onRetry={() => void report.refetch()} />;
  const { rows, totals, seesMargin, seesPay } = report.data;

  const columns: Column<TripsReportRow>[] = [
    {
      label: 'Курс',
      cell: (r) => (
        <Link to="/trips/$tripId" params={{ tripId: r.tripId }} className="font-medium hover:text-accent">
          {r.number}
        </Link>
      ),
      csv: (r) => r.number,
      className: 'whitespace-nowrap',
    },
    { label: 'Тръгване', cell: (r) => formatDate(r.startAt), csv: (r) => formatDate(r.startAt), className: 'whitespace-nowrap' },
    {
      label: 'Състояние',
      cell: (r) => <StatusPill kind="trip" status={r.status as TripStatus} />,
      csv: (r) => TRIP_STATUS_BG[r.status as TripStatus] ?? r.status,
    },
    { label: 'Бус', cell: (r) => r.vehicle ?? '—', csv: (r) => r.vehicle, className: 'whitespace-nowrap' },
    { label: 'Шофьор', cell: (r) => r.driver ?? '—', csv: (r) => r.driver, className: 'whitespace-nowrap' },
    {
      label: 'Маршрут',
      cell: (r) => (r.from || r.to ? `${r.from ?? '?'} → ${r.to ?? '?'}` : '—'),
      csv: (r) => (r.from || r.to ? `${r.from ?? '?'} → ${r.to ?? '?'}` : ''),
      className: 'min-w-[12rem]',
    },
    { label: 'Клиенти', cell: (r) => r.clients ?? '—', csv: (r) => r.clients, className: 'max-w-[14rem] truncate' },
    { label: 'Заявки', numeric: true, cell: (r) => r.orders, csv: (r) => r.orders, total: formatNumber(totals.orders), csvTotal: totals.orders },
    {
      label: 'Документи',
      numeric: true,
      cell: (r) => (
        <span className={r.orders > 0 && r.docsComplete < r.orders ? 'text-warn' : undefined}>
          {r.docsComplete}/{r.orders}
        </span>
      ),
      csv: (r) => `${r.docsComplete}/${r.orders}`,
      total: `${totals.ordersDocsComplete}/${totals.orders}`,
      csvTotal: `${totals.ordersDocsComplete}/${totals.orders}`,
    },
    { label: 'Тегло', numeric: true, cell: (r) => formatKg(r.weightKg), csv: (r) => r.weightKg },
    { label: 'Пълнота', numeric: true, cell: (r) => pct(r.loadPct), csv: (r) => r.loadPct },
    { label: 'Км', numeric: true, cell: (r) => formatKm(r.km), csv: (r) => r.km, total: formatKm(totals.km), csvTotal: totals.km },
    { label: 'Празни км', numeric: true, cell: (r) => formatKm(r.emptyKm), csv: (r) => r.emptyKm, total: formatKm(totals.emptyKm), csvTotal: totals.emptyKm },
    { label: 'Приход', numeric: true, cell: (r) => money(r.revenueMinor), csv: (r) => euros(r.revenueMinor), total: money(totals.revenueMinor), csvTotal: euros(totals.revenueMinor) },
    { label: 'Гориво', numeric: true, hidden: !seesMargin, cell: (r) => money(r.fuelMinor), csv: (r) => euros(r.fuelMinor), total: money(totals.fuelMinor), csvTotal: euros(totals.fuelMinor) },
    { label: 'Такси', numeric: true, hidden: !seesMargin, cell: (r) => money(r.tollsMinor), csv: (r) => euros(r.tollsMinor), total: money(totals.tollsMinor), csvTotal: euros(totals.tollsMinor) },
    { label: 'Други', numeric: true, hidden: !seesMargin, cell: (r) => money(r.otherMinor), csv: (r) => euros(r.otherMinor), total: money(totals.otherMinor), csvTotal: euros(totals.otherMinor) },
    { label: 'Заплата', numeric: true, hidden: !seesPay, cell: (r) => money(r.payMinor), csv: (r) => euros(r.payMinor), total: money(totals.payMinor), csvTotal: euros(totals.payMinor) },
    { label: 'Марж', numeric: true, hidden: !seesMargin, cell: (r) => signed(r.marginMinor), csv: (r) => euros(r.marginMinor), total: signed(totals.marginMinor), csvTotal: euros(totals.marginMinor) },
  ];

  return (
    <ReportTable
      title="По курсове"
      subtitle={
        seesMargin
          ? 'Маржът е приходът минус горивото, таксите, другите разходи по курса и заплатата на шофьора (км × ставката му).'
          : 'Приходът е цената на заявките в курса. Разходите и маржът вижда управителят.'
      }
      fileName={`spravka-kursove-${from}-${to}.csv`}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.tripId}
      actions={
        <>
          <Select
            className="h-8 w-36 text-xs"
            aria-label="Бус"
            value={filters.vehicleId}
            onChange={(e) => setFilters({ ...filters, vehicleId: e.target.value })}
            placeholder="Всички бусове"
            options={(vehicles.data?.items ?? []).map((v) => ({ value: v.id, label: `${v.fleetNo} · ${v.plate}` }))}
          />
          <Select
            className="h-8 w-40 text-xs"
            aria-label="Шофьор"
            value={filters.driverId}
            onChange={(e) => setFilters({ ...filters, driverId: e.target.value })}
            placeholder="Всички шофьори"
            options={(drivers.data?.items ?? []).map((d) => ({ value: d.id, label: d.fullName || `${d.firstName} ${d.lastName}` }))}
          />
          <Select
            className="h-8 w-44 text-xs"
            aria-label="Клиент"
            value={filters.partnerId}
            onChange={(e) => setFilters({ ...filters, partnerId: e.target.value })}
            placeholder="Всички клиенти"
            options={(partners.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }))}
          />
        </>
      }
    />
  );
}

/** ─── По клиенти ─────────────────────────────────────────────────────── */

export function CustomersReportView({ from, to }: { from: string; to: string }) {
  const report = useCustomersReport(from, to);
  if (report.isLoading) return <LoadingBlock />;
  if (report.isError || !report.data) return <ErrorState onRetry={() => void report.refetch()} />;
  const { rows, totals, seesMargin, seesInvoices } = report.data;

  const columns: Column<CustomersReportRow>[] = [
    { label: 'Клиент', cell: (r) => <span className="font-medium">{r.name}</span>, csv: (r) => r.name, className: 'max-w-[16rem] truncate' },
    { label: 'Държава', cell: (r) => (r.country ? countryName(r.country) : '—'), csv: (r) => r.country },
    { label: 'Заявки', numeric: true, cell: (r) => r.orders, csv: (r) => r.orders, total: formatNumber(totals.orders), csvTotal: totals.orders },
    { label: 'Доставени', numeric: true, cell: (r) => r.delivered, csv: (r) => r.delivered },
    {
      label: 'Отказани',
      numeric: true,
      cell: (r) => <span className={r.cancelled > 0 ? 'text-warn' : undefined}>{r.cancelled}</span>,
      csv: (r) => r.cancelled,
    },
    { label: 'Тегло', numeric: true, cell: (r) => formatKg(r.weightKg), csv: (r) => r.weightKg },
    { label: 'Приход', numeric: true, cell: (r) => money(r.revenueMinor), csv: (r) => euros(r.revenueMinor), total: money(totals.revenueMinor), csvTotal: euros(totals.revenueMinor) },
    { label: 'Км', numeric: true, cell: (r) => formatKm(r.km), csv: (r) => r.km, total: formatKm(totals.km), csvTotal: totals.km },
    {
      label: '€/км',
      numeric: true,
      cell: (r) => (r.revenuePerKmMinor === null ? '—' : formatMinor(r.revenuePerKmMinor, 'EUR')),
      csv: (r) => euros(r.revenuePerKmMinor),
    },
    { label: 'Марж', numeric: true, hidden: !seesMargin, cell: (r) => signed(r.marginMinor), csv: (r) => euros(r.marginMinor), total: signed(totals.marginMinor), csvTotal: euros(totals.marginMinor) },
    {
      label: 'Навреме',
      numeric: true,
      cell: (r) => (
        <span className={r.onTimePct !== null && r.onTimePct < 85 ? 'text-warn' : undefined} title={`${r.deliveriesMeasured} доставки с час`}>
          {pct(r.onTimePct)}
        </span>
      ),
      csv: (r) => r.onTimePct,
    },
    { label: 'Дължи', numeric: true, hidden: !seesInvoices, cell: (r) => money(r.outstandingMinor), csv: (r) => euros(r.outstandingMinor), total: money(totals.outstandingMinor), csvTotal: euros(totals.outstandingMinor) },
    {
      label: 'Просрочено',
      numeric: true,
      hidden: !seesInvoices,
      cell: (r) => <span className={(r.overdueMinor ?? 0) > 0 ? 'text-danger' : undefined}>{money(r.overdueMinor)}</span>,
      csv: (r) => euros(r.overdueMinor),
      total: <span className="text-danger">{money(totals.overdueMinor)}</span>,
      csvTotal: euros(totals.overdueMinor),
    },
    { label: 'Плаща за', numeric: true, hidden: !seesInvoices, cell: (r) => (r.avgDaysToPay === null ? '—' : `${r.avgDaysToPay} дни`), csv: (r) => r.avgDaysToPay },
    { label: 'Последна заявка', cell: (r) => formatDate(r.lastOrderAt), csv: (r) => formatDate(r.lastOrderAt), className: 'whitespace-nowrap' },
  ];

  return (
    <ReportTable
      title="По клиенти"
      subtitle={
        seesInvoices
          ? '„Дължи" и „Просрочено" са към днес, не за периода. „Плаща за" — средно дни от фактура до плащане за последната година.'
          : undefined
      }
      fileName={`spravka-klienti-${from}-${to}.csv`}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.partnerId}
    />
  );
}

/** ─── По шофьори ─────────────────────────────────────────────────────── */

export function DriversReportView({ from, to }: { from: string; to: string }) {
  const report = useDriversReport(from, to);
  if (report.isLoading) return <LoadingBlock />;
  if (report.isError || !report.data) return <ErrorState onRetry={() => void report.refetch()} />;
  const { rows, totals, seesPay } = report.data;
  const missingRates = rows.filter((r) => r.rateMissing).length;

  const columns: Column<DriversReportRow>[] = [
    {
      label: 'Шофьор',
      cell: (r) => (
        <Link to="/drivers/$driverId" params={{ driverId: r.driverId }} className="font-medium hover:text-accent">
          {r.name}
        </Link>
      ),
      csv: (r) => r.name,
      className: 'whitespace-nowrap',
    },
    { label: 'Курсове', numeric: true, cell: (r) => r.trips, csv: (r) => r.trips, total: formatNumber(totals.trips), csvTotal: totals.trips },
    { label: 'Км', numeric: true, cell: (r) => formatKm(r.km), csv: (r) => r.km, total: formatKm(totals.km), csvTotal: totals.km },
    { label: 'Дни на път', numeric: true, cell: (r) => r.days, csv: (r) => r.days, total: formatNumber(totals.days), csvTotal: totals.days },
    { label: 'Доставки', numeric: true, cell: (r) => r.deliveries, csv: (r) => r.deliveries, total: formatNumber(totals.deliveries), csvTotal: totals.deliveries },
    {
      label: 'Закъснели',
      numeric: true,
      cell: (r) => <span className={r.lateDeliveries > 0 ? 'text-warn' : undefined}>{r.lateDeliveries}</span>,
      csv: (r) => r.lateDeliveries,
      total: formatNumber(totals.lateDeliveries),
      csvTotal: totals.lateDeliveries,
    },
    {
      label: 'Произшествия',
      numeric: true,
      cell: (r) => (
        <span className={r.accidents > 0 ? 'text-danger' : undefined}>
          {r.incidents}
          {r.accidents ? ` (ПТП ${r.accidents})` : ''}
        </span>
      ),
      csv: (r) => r.incidents,
      total: formatNumber(totals.incidents),
      csvTotal: totals.incidents,
    },
    { label: 'Върнати снимки', numeric: true, cell: (r) => r.returnedDocuments, csv: (r) => r.returnedDocuments },
    {
      label: 'Заплата (км)',
      numeric: true,
      hidden: !seesPay,
      cell: (r) => (r.rateMissing ? <span className="text-warn">няма ставка</span> : money(r.payMinor)),
      csv: (r) => euros(r.payMinor),
      total: money(totals.payMinor),
      csvTotal: euros(totals.payMinor),
    },
  ];

  return (
    <ReportTable
      title="По шофьори"
      subtitle={
        seesPay && missingRates
          ? `${missingRates} шофьор${missingRates === 1 ? '' : 'и'} без ставка на км — заплатата им не е сметната. Ставката се въвежда в „Шофьори".`
          : 'Курсовете и километрите са на приключените курсове; километрите — по километража, иначе по GPS.'
      }
      fileName={`spravka-shofyori-${from}-${to}.csv`}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.driverId}
    />
  );
}

/** ─── По бусове ──────────────────────────────────────────────────────── */

export function VansReportView({ from, to }: { from: string; to: string }) {
  const report = useVansReport(from, to);
  if (report.isLoading) return <LoadingBlock />;
  if (report.isError || !report.data) return <ErrorState onRetry={() => void report.refetch()} />;
  const { rows, totals, seesMargin } = report.data;

  const columns: Column<VansReportRow>[] = [
    {
      label: 'Бус',
      cell: (r) => (
        <Link to="/vehicles/$vehicleId" params={{ vehicleId: r.vehicleId }} className="whitespace-nowrap hover:text-accent">
          <span className="font-display font-semibold">{r.fleetNo}</span>
          <span className="ml-1.5 font-mono text-xs text-ink-dim">{r.plate}</span>
        </Link>
      ),
      csv: (r) => `${r.fleetNo} ${r.plate}`,
    },
    { label: 'Курсове', numeric: true, cell: (r) => r.trips, csv: (r) => r.trips, total: formatNumber(totals.trips), csvTotal: totals.trips },
    { label: 'Км', numeric: true, cell: (r) => formatKm(r.km), csv: (r) => r.km, total: formatKm(totals.km), csvTotal: totals.km },
    {
      label: 'Празни',
      numeric: true,
      cell: (r) => <span className={r.emptyPct !== null && r.emptyPct > 30 ? 'text-warn' : undefined}>{pct(r.emptyPct)}</span>,
      csv: (r) => r.emptyPct,
      total: pct(totals.km > 0 ? Math.round((totals.emptyKm * 100) / totals.km) : null),
    },
    {
      label: 'Дни в движение',
      numeric: true,
      cell: (r) => (
        <span title={`${r.utilisationPct} % от дните в периода`}>
          {r.daysUsed} <span className="text-[11px] text-ink-faint">({r.utilisationPct} %)</span>
        </span>
      ),
      csv: (r) => r.daysUsed,
    },
    { label: 'Приход', numeric: true, cell: (r) => money(r.revenueMinor), csv: (r) => euros(r.revenueMinor), total: money(totals.revenueMinor), csvTotal: euros(totals.revenueMinor) },
    { label: '€/км', numeric: true, cell: (r) => (r.revenuePerKmMinor === null ? '—' : formatMinor(r.revenuePerKmMinor, 'EUR')), csv: (r) => euros(r.revenuePerKmMinor) },
    {
      label: 'Гориво л/100',
      numeric: true,
      cell: (r) => (r.litresPer100 === null ? '—' : r.litresPer100.toLocaleString('bg-BG')),
      csv: (r) => r.litresPer100,
    },
    { label: 'Разходи', numeric: true, hidden: !seesMargin, cell: (r) => money(r.costMinor), csv: (r) => euros(r.costMinor), total: money(totals.costMinor), csvTotal: euros(totals.costMinor) },
    {
      label: 'Разход/км',
      numeric: true,
      hidden: !seesMargin,
      cell: (r) => (r.costPerKmMinor === null ? '—' : formatMinor(r.costPerKmMinor, 'EUR')),
      csv: (r) => euros(r.costPerKmMinor),
    },
    { label: 'Марж', numeric: true, hidden: !seesMargin, cell: (r) => signed(r.marginMinor), csv: (r) => euros(r.marginMinor), total: signed(totals.marginMinor), csvTotal: euros(totals.marginMinor) },
    { label: 'Ремонти', numeric: true, cell: (r) => r.repairs, csv: (r) => r.repairs },
  ];

  return (
    <ReportTable
      title="По бусове"
      subtitle={[
        'Дни в движение — дни с курс в ход от дните в периода. Горивото е от картите (литри) спрямо изминатите км.',
        seesMargin ? 'Разходите са всички разходи на буса за периода — гориво, такси, ремонти, други.' : null,
      ]
        .filter(Boolean)
        .join(' ')}
      fileName={`spravka-busove-${from}-${to}.csv`}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.vehicleId}
    />
  );
}
